/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import Joi from 'joi';
import Cookies from 'js-cookie';
import { signIn, teamSignIn } from '../../services/auth.service';
import { setStorage } from '../../utils/auth-utils';
import { ciIdentify } from '../../config/axios-config';
import { capitalizeFirstLetter, passwordRegex, triggerGTMViewPageEvent } from '../../utils/global';
import { getTrialDaysCount } from '../../services/subscription.service';
import Testimonials from './components/Testimonials';
import ButtonComponent from '../common-components/ButtonComponent/ButtonComponent';
import ThreeDotsLoader from '../common-components/Loaders/ThreeDotsLoader';
import Saral from '../../images/Saral-bg.png';
import { VisibilityIcon, VisibilityOffIcon, CopyrightIcon, MailIcon } from '../Icons';
import './styles/register.scss';
import './styles/login.scss';
import { Mixpanel } from '../../utils/mixpanel';
import useQueryParam from '@/hooks/useQueryParam';

const registerSchema = Joi.object({
  password: Joi.string().regex(passwordRegex).max(300).required().messages({
    'string.pattern.base':
      'Password should contain minimum 6 Characters with minimum 1 Uppercase Character  and 1 Number',
    'any.required': 'Please enter a valid password',
    'string.max': "Password can't be more than 300 characters long",
  }),
  name: Joi.string().min(3).max(100).required().messages({
    'string.min': 'Please enter your full name',
    'string.max': 'Please reduce the size of your name',
    'any.required': 'Name is required',
  }),
});

export default function RegisterTeam() {
  const queryParam = useQueryParam();

  const [name, setName] = useState(queryParam.get('name') || null);
  const [email, setEmail] = useState(queryParam.get('email') || null);

  const [code, setCode] = useState(queryParam.get('code') || null);
  const [teamMemberId, setTeamMemberId] = useState(queryParam.get('teamMemberId') || null);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [re_password, setRePassword] = useState('');
  const [showRePassword, setShowRePassword] = useState(false);
  const [password_error, setPasswordError] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  let referral = localStorage.getItem('referral');
  referral = referral ? JSON.parse(referral) : {};

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const primary_member_name = queryParam.get('primary_member_name');

  const validateData = async (body) => {
    if (password.startsWith(' ')) {
      setErrors({ password: "For ease of use, don't add a space at the start of your password" });
      return false;
    } else if (password.endsWith(' ')) {
      setErrors({ password: "For ease of use, don't add a space at the end of your password" });
      return false;
    }
    const { error } = registerSchema.validate(body, { abortEarly: false });
    if (error) {
      const validationErrors = {};
      error.details.forEach((detail) => {
        validationErrors[detail.context.key] = detail.message;
      });
      setErrors(validationErrors);
      return false;
    } else {
      return true;
    }
  };

  const registerUser = async (e) => {
    e.preventDefault();
    const body = { name: name.trim(), password };
    const isValid = await validateData(body);
    if (disableSubmitButton) {
      return;
    }
    if (password !== re_password) {
      setPasswordError(true);
    }

    if (isValid && password === re_password) {
      setDisableSubmitButton(true);
      const response = await teamSignIn(body, teamMemberId, code);
      if (response?.status) {
        NotificationManager.success('You Registered Successfully', '', 1200);
        setStorage('token', response.data.token);
        Cookies.set('access_token', response.data.token, { expires: 15 });
        const firstName = capitalizeFirstLetter(body?.name?.split(' ')[0]);
        const lastName = body?.name.slice(body?.name.split(' ')[0].length).trim() || '';
        Mixpanel.identify(response?.data?.userId);
        Mixpanel.people.set({
          $first_name: firstName,
          $last_name: lastName,
          $distinct_id: response?.data?.userId,
          $email: body?.email,
          $created: Math.floor(Date.now() / 1000),
          'Unique User ID': response?.data?.userId,
        });
        Mixpanel.track('sign_up');
        ciIdentify({
          id: response?.data?.userId,
          email: body?.email,
          subscription_status: 'free',
          is_subscribed: false,
          first_name: firstName,
          last_name: lastName,
          is_payment_method_added: false,
          created_at: Math.floor(Date.now() / 1000),
          _timestamp: Math.floor(Date.now() / 1000),
        });
        setTimeout(() => {
          window.location.href = import.meta.env.REACT_APP_DOMAIN;
        }, 1000);
      } else {
        if (response?.blocked) {
          navigate('/error');
          return;
        }
        NotificationManager.error(response?.data || 'Error While Registering', '', 1200);
        setDisableSubmitButton(false);
      }
    } else {
      setDisableSubmitButton(false);
    }
  };

  const getFreeTrialDaysCount = async () => {
    const body = { ...referral };
    const response = await getTrialDaysCount(body);
    if (response?.status) {
    }
  };

  useEffect(() => {
    getFreeTrialDaysCount();
    triggerGTMViewPageEvent('Register');
  }, []);

  return (
    <div className="login-main-div">
      <div className="login-form">
        <img src={Saral} alt="" width={160} className="saral-logo" />
        <h2>
          You're invited to SARAL by <br /> {primary_member_name}
        </h2>
        <form>
          <div className="form-group">
            <label className="required-field">Name</label>
            <input
              type="text"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => {
                setErrors({});
                setName(e.target.value);
              }}
              style={{ borderColor: errors.name ? 'var(--red)' : '#d0d5dd' }}
            />
            {errors.name && <div className="errorMessage">{errors.name}</div>}
          </div>
          <div className="form-group">
            <label className="required-field">Email</label>
            <input
              type="text"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setErrors({});
                setEmail(e.target.value);
              }}
              disabled={true}
              style={{
                borderColor: errors.email ? 'var(--red)' : '#d0d5dd',
                backgroundColor: 'var(--background-default)',
              }}
            />
            {errors.email && <div className="errorMessage">{errors.email}</div>}
          </div>
          <div className="form-group">
            <label className="required-field">Password</label>
            <div className="input-div">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setErrors({});
                  setPassword(e.target.value);
                  setPasswordError(false);
                }}
                style={{ borderColor: password_error ? 'var(--red)' : '#d0d5dd', paddingRight: 40 }}
              />
              <button type="button" className="show-password-btn" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </button>
            </div>
            {errors.password && <div className="errorMessage">{errors.password}</div>}
          </div>
          <div className="form-group">
            <label className="required-field">Repeat Password</label>
            <div className="input-div">
              <input
                type={showRePassword ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={re_password}
                onChange={(e) => {
                  setErrors({});
                  setRePassword(e.target.value);
                  setPasswordError(false);
                }}
                style={{ borderColor: password_error ? 'var(--red)' : '#d0d5dd', paddingRight: 40 }}
              />
              <button type="button" className="show-password-btn" onClick={() => setShowRePassword(!showRePassword)}>
                {showRePassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </button>
            </div>
            {password_error === true && re_password !== null && (
              <p className="error-message">The password does not match</p>
            )}
          </div>
          {/* <div className="label-div">
              <label>
                <input type="checkbox" checked={isChecked} onChange={() => setIsChecked(!isChecked)} />I agree to the{' '}
                <a href="https://www.getsaral.com/terms" target="_blank" rel="noopener noreferrer">
                  terms & conditions
                </a>
              </label>
            </div> */}
          {!name || !email || !password || !re_password || disableSubmitButton ? (
            <ButtonComponent type="button" variant="primary">
              {disableSubmitButton && <ThreeDotsLoader />}
              Create Account
            </ButtonComponent>
          ) : (
            <ButtonComponent variant="primary" onClick={registerUser}>
              Create Account
            </ButtonComponent>
          )}
        </form>
        <p className="login-text">
          Already have an account? <span onClick={() => navigate('/')}>Log in</span>
        </p>
      </div>
    </div>
  );
}
